import { useEffect, useState, useCallback } from "react";
import { listSessions, archiveSession, type Session } from "../lib/api";

/** UC15 — sidebar of all active chats with new/select; archive (UC18). */
export function ChatList({
  currentId,
  refreshKey,
  onSelect,
  onNew,
  onChanged,
}: {
  currentId: string | null;
  refreshKey: number;
  onSelect: (id: string) => void;
  onNew: () => void;
  onChanged: () => void;
}) {
  const [sessions, setSessions] = useState<Session[]>([]);

  const load = useCallback(async () => {
    setSessions(await listSessions());
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  async function archive(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    await archiveSession(id);
    await load();
    onChanged();
  }

  function label(s: Session): string {
    if (s.last_distilled_at) {
      return `aktualisiert ${new Date(s.last_distilled_at).toLocaleString("de-DE", {
        dateStyle: "short",
        timeStyle: "short",
      })}`;
    }
    return "noch nicht destilliert";
  }

  return (
    <nav className="flex h-full flex-col border-r border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950">
      <div className="p-2">
        <button
          data-testid="newChatBtn"
          onClick={onNew}
          className="w-full rounded-md bg-blue-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
        >
          Neue Sitzung
        </button>
      </div>
      <div data-testid="chatList" className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2 pb-2">
        {sessions.length === 0 && (
          <p className="px-1 text-xs text-neutral-400">Noch keine Sitzungen.</p>
        )}
        {sessions.map((s) => (
          <div
            key={s.id}
            data-testid="chatListItem"
            onClick={() => onSelect(s.id)}
            className={
              "group flex cursor-pointer items-start gap-1 rounded-md px-2 py-1.5 text-left text-xs " +
              (s.id === currentId
                ? "bg-blue-100 dark:bg-blue-950"
                : "hover:bg-neutral-200 dark:hover:bg-neutral-800")
            }
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">
                {new Date(s.created_at).toLocaleString("de-DE", {
                  dateStyle: "short",
                  timeStyle: "short",
                })}
              </div>
              <div className="truncate text-[10px] text-neutral-500">{label(s)}</div>
            </div>
            <button
              data-testid="archiveBtn"
              title="Archivieren"
              onClick={(e) => archive(e, s.id)}
              className="rounded px-1 text-[10px] text-neutral-400 opacity-60 hover:bg-neutral-300 hover:opacity-100 dark:hover:bg-neutral-700"
            >
              Archiv
            </button>
          </div>
        ))}
      </div>
    </nav>
  );
}
